// ---------------------------------------------------------------------------
// brendaStock — stock / branch selection helpers
// ---------------------------------------------------------------------------
//
// Fresh copies of the stock-and-branch rules previously imported from
// `app/utils/stock.ts` and `app/utils/product.ts`. The new charges flow
// depends only on these so rule changes can be made here without
// touching legacy code paths.
//
// Two concerns live in this file:
//   1. Stock / date resolution for a single side (delivery or pickup)
//      — `resolveBrendaDeliveryStock` and `resolveBrendaPickupStock`.
//   2. Branch selection from a raw charges response — `findBrendaNearestPickup`
//      and `findBrendaPickupByBranchId`.
// ---------------------------------------------------------------------------

// Types
import type { GetPricesHireData, PickupPoint as RawPickupPoint } from '~/types'
import type {
  BrendaChargesExtra,
  BrendaChargesPickupPoint,
} from '~/types/brendaCharges'

// ---------------------------------------------------------------------------
// Fallback caps
// ---------------------------------------------------------------------------

const BULK_FALLBACK = 10
const CONSUMABLE_FALLBACK = 1000

/**
 * Default max-quantity cap by product type. Hire (`bulk`) products are
 * capped lower than consumables.
 */
export const getBrendaMaxQtyFallback = (productType?: string): number =>
  productType === 'consumable' ? CONSUMABLE_FALLBACK : BULK_FALLBACK

/**
 * Caps a stock count to the fallback; returns the fallback when the
 * incoming stock value is missing / zero / non-finite.
 */
export const capBrendaStock = (
  stock: number | null | undefined,
  fallback: number
): number => {
  const n = Number(stock)
  return Number.isFinite(n) && n > 0 ? Math.min(n, fallback) : fallback
}

// ---------------------------------------------------------------------------
// Delivery stock rules
// ---------------------------------------------------------------------------

export interface BrendaDeliveryStockResult {
  available: boolean
  maxQuantity: number
  date: string | null
}

/**
 * Resolves delivery availability + max quantity.
 *
 * Brenda always returns a `deliveryDate` even for out-of-stock items,
 * so `deliveryDate` on its own can't be trusted. `firstAvailableDate`
 * is the signal that the product is a future-stock one, and
 * `siteWithStock` is the signal for current-stock accuracy.
 *
 * Paths run in strict priority — the first match wins:
 *
 *   1. `firstAvailableDate` present → future-stock product. Uses the
 *      future date and the product-type fallback max quantity (Brenda
 *      doesn't carry a per-future-date count).
 *   2. `siteWithStock` present on the response:
 *        • `maxQuantity > 0` → in stock with that exact value (capped
 *          to the fallback). Date prefers `deliveryDate`, falling
 *          back to the caller-supplied `requestedDate` if Brenda
 *          didn't return one.
 *        • `maxQuantity === 0` → not in stock (falls through to 4).
 *   3. `siteWithStock` missing entirely → trust the delivery response
 *      and use the fallback stock value. Date prefers `deliveryDate`,
 *      falling back to `requestedDate`.
 *   4. Otherwise → out of stock.
 *
 * `deliveryStock` typing: `null` / `undefined` = "siteWithStock was
 * missing from the response"; a `number` (including `0`) = "present
 * with that value".
 */
export const resolveBrendaDeliveryStock = (
  productType: string | undefined,
  deliveryStock: number | null | undefined,
  deliveryDate: string | null | undefined,
  firstAvailableDate: string | null | undefined,
  requestedDate?: string | null
): BrendaDeliveryStockResult => {
  const fallback = getBrendaMaxQtyFallback(productType)

  // 1. Future-stock product — firstAvailableDate takes priority over
  //    any siteWithStock number. The presence of this field means
  //    Brenda is telling us the real date is a future one, so we
  //    trust it over whatever current-stock figure may also be there.
  if (firstAvailableDate) {
    return {
      available: true,
      maxQuantity: fallback,
      date: firstAvailableDate,
    }
  }

  const siteWithStockPresent =
    typeof deliveryStock === 'number' && Number.isFinite(deliveryStock)

  // 2. siteWithStock present AND positive → exact current stock.
  if (siteWithStockPresent && (deliveryStock as number) > 0) {
    const date = deliveryDate || requestedDate
    if (date) {
      return {
        available: true,
        maxQuantity: Math.min(Math.floor(deliveryStock as number), fallback),
        date,
      }
    }
  }

  // 3. siteWithStock missing entirely → fallback max quantity. This
  //    catches responses where Brenda omits stock info but still
  //    wants delivery to go ahead (e.g. some product classes that
  //    don't carry a per-site count).
  if (!siteWithStockPresent) {
    const date = deliveryDate || requestedDate
    if (date) {
      return {
        available: true,
        maxQuantity: fallback,
        date,
      }
    }
  }

  // 4. Out of stock (siteWithStock present with zero, or no usable
  //    date could be resolved for an otherwise-available entry).
  return {
    available: false,
    maxQuantity: 0,
    date: null,
  }
}

// ---------------------------------------------------------------------------
// Pickup stock rules
// ---------------------------------------------------------------------------

export interface BrendaPickupStockResult {
  available: boolean
  maxQuantity: number
  date: string | null
  /**
   * True when `maxQuantity` is the product-type fallback rather than a
   * known exact count — i.e. future stock (no per-date count) or current
   * stock that exceeded the cap. Lets the UI show "Stock available"
   * instead of a misleading "{fallback} in stock".
   */
  isFallback: boolean
}

/**
 * Resolves pickup availability + max quantity for a chosen branch.
 * Paths run in strict order — the first one to match wins:
 *
 *   1. `firstAvailableDate` present → future-stock branch. The
 *      presence of this field is Brenda's signal that the branch
 *      isn't stocked *now*, so it takes priority over whatever
 *      current `quantity` value may also be returned (which reflects
 *      current stock and may be stale / irrelevant in this case).
 *      Uses the fallback max quantity (Brenda doesn't carry a
 *      per-future-date count).
 *   2. `quantity > 0` → in stock now. Uses the requested date and the
 *      exact quantity value (capped to fallback).
 *   3. Otherwise → out of stock.
 */
export const resolveBrendaPickupStock = (
  productType: string | undefined,
  quantity: number | null | undefined,
  firstAvailableDate: string | null | undefined,
  requestedDate: string | null | undefined
): BrendaPickupStockResult => {
  const fallback = getBrendaMaxQtyFallback(productType)

  // 1. Future-stock branch — firstAvailableDate takes priority over
  //    any current-stock quantity value on the same branch. No exact
  //    per-future-date count, so this is always the fallback.
  if (firstAvailableDate) {
    return {
      available: true,
      maxQuantity: fallback,
      date: firstAvailableDate,
      isFallback: true,
    }
  }

  // 2. Current stock — exact quantity at the requested date, capped
  //    to the product-type fallback. When the real count met or
  //    exceeded the cap, the displayed value is the fallback (not exact).
  const hasStock = typeof quantity === 'number' && quantity > 0
  if (hasStock && requestedDate) {
    const exact = Math.floor(quantity as number)
    return {
      available: true,
      maxQuantity: Math.min(exact, fallback),
      date: requestedDate,
      isFallback: exact >= fallback,
    }
  }

  // 3. Out of stock.
  return {
    available: false,
    maxQuantity: 0,
    date: null,
    isFallback: false,
  }
}

// ---------------------------------------------------------------------------
// Pickup branch selection
// ---------------------------------------------------------------------------

/**
 * Normalises a `quantity` value (`string | number`) to the required
 * integer count used when checking a branch's stock, always at least 1.
 */
const getRequiredQty = (quantity: number | string): number => {
  const n = Number(quantity)
  return Math.max(1, Number.isFinite(n) ? Math.ceil(n) : 1)
}

/**
 * Collects the stock codes of mandatory linked add-ons (hire + resale)
 * from a charges response. Used to gate branch selection: a branch
 * isn't viable if a mandatory add-on has insufficient stock there.
 */
const collectMandatoryStockCodes = (charges: GetPricesHireData): string[] => {
  return [
    ...(charges.linkedHireProducts ?? []),
    ...(charges.linkedResaleProducts ?? []),
  ]
    .filter(linked => linked?.isMandatory)
    .map(linked => String(linked.product?.stockCode ?? ''))
    .filter(stockCode => stockCode.length > 0)
}

/**
 * Checks that every mandatory add-on has sufficient stock at the given
 * branch. Returns `true` when there are no mandatory add-ons to worry
 * about.
 */
const branchHasMandatoryStock = (
  branch: RawPickupPoint,
  required: number,
  mandatoryStockCodes: string[]
): boolean => {
  if (mandatoryStockCodes.length === 0) return true

  const linked = branch.linkedProductAvailability ?? []

  return mandatoryStockCodes.every(stockCode => {
    const match = linked.find(l => l.stockCode === stockCode)
    return (match?.maxQuantity ?? 0) >= required
  })
}

/**
 * Branch has current stock for the parent AND every mandatory add-on
 * for the requested quantity. A branch with `firstAvailableDate` is
 * *not* counted as current-stock here — the presence of that field is
 * Brenda's signal that the branch is a future-stock one, and takes
 * priority over whatever `quantity` value may also be returned.
 */
const branchHasCurrentStock = (
  branch: RawPickupPoint,
  required: number,
  mandatoryStockCodes: string[]
): boolean => {
  return (
    !branch?.firstAvailableDate &&
    Number(branch?.quantity ?? 0) >= required &&
    branchHasMandatoryStock(branch, required, mandatoryStockCodes)
  )
}

/**
 * Branch has a `firstAvailableDate` — i.e. Brenda has flagged it as a
 * future-stock branch. We do *not* re-check mandatory add-on stock
 * here: `linkedProductAvailability` is a current-stock view, and we
 * have no per-branch view of what add-on availability will look like
 * on the parent's future date. Trust Brenda — if the parent resolves
 * on a future date, the add-ons are assumed to follow.
 */
const branchHasFutureStock = (branch: RawPickupPoint): boolean => {
  return !!branch?.firstAvailableDate
}

/**
 * Loops over the pickup points on a charges response and returns the
 * first viable branch — prioritising branches with current stock, then
 * branches with future stock. `null` when nothing usable.
 */
export const findBrendaNearestPickup = (
  charges: GetPricesHireData,
  quantity: number | string
): RawPickupPoint | null => {
  const points = charges.pickupPoints ?? []
  if (points.length === 0) return null

  const required = getRequiredQty(quantity)
  const mandatory = collectMandatoryStockCodes(charges)

  const inStock = points.find(p =>
    branchHasCurrentStock(p, required, mandatory)
  )
  if (inStock) return inStock

  const future = points.find(p => branchHasFutureStock(p))
  if (future) return future

  return null
}

/**
 * Returns a specific branch by id if it's still viable for the
 * requested quantity. Accepts both current-stock and future-stock
 * branches — `firstAvailableDate` takes precedence over the live
 * stock check, mirroring `resolveBrendaPickupStock` /
 * `checkBrendaPickupPointStock`.
 *
 * Falls back to `findBrendaNearestPickup` when the specified branch
 * isn't viable — unless `strict` is set, in which case it returns
 * `null` and the caller treats the pickup as unavailable. Strict mode
 * is for flows like the pickup modal where the user picked a specific
 * branch and we must not silently reassign them.
 */
export const findBrendaPickupByBranchId = (
  charges: GetPricesHireData,
  quantity: number | string,
  branchId: number | null | undefined,
  strict?: boolean
): RawPickupPoint | null => {
  const points = charges.pickupPoints ?? []
  if (points.length === 0) return null

  const required = getRequiredQty(quantity)
  const mandatory = collectMandatoryStockCodes(charges)

  const match =
    branchId != null ? points.find(p => p.id === branchId) : undefined

  if (
    match &&
    (branchHasFutureStock(match) ||
      branchHasCurrentStock(match, required, mandatory))
  ) {
    return match
  }

  if (strict) return null

  return findBrendaNearestPickup(charges, quantity)
}

// ---------------------------------------------------------------------------
// Branch display helpers
// ---------------------------------------------------------------------------

/**
 * Builds the joined address string used when writing a branch to a
 * basket line. Combines supplier name + address line + city + postcode
 * in display order.
 */
export const formatBrendaBranchAddress = (
  branch: RawPickupPoint | null | undefined
): string | null => {
  if (!branch) return null
  const siteLocation = branch.siteLocation
  return [
    branch.customerSupplierName,
    siteLocation?.address1,
    siteLocation?.city,
    siteLocation?.postcode,
  ]
    .filter(Boolean)
    .join(', ')
}

// ---------------------------------------------------------------------------
// `BrendaChargesPickupPoint` helpers (consumer-facing)
// ---------------------------------------------------------------------------

/**
 * Builds the joined address string from a remapped
 * `BrendaChargesPickupPoint`. Mirrors `formatBrendaBranchAddress` but
 * takes the minimally-remapped display shape exposed on
 * `charges.product.pickupPoints`.
 */
export const formatBrendaChargesBranchAddress = (
  point: BrendaChargesPickupPoint | null | undefined
): string | null => {
  if (!point) return null
  const siteLocation = point.siteLocation
  return [
    point.customerSupplierName,
    siteLocation?.address1,
    siteLocation?.city,
    siteLocation?.postcode,
  ]
    .filter(Boolean)
    .join(', ')
}

/**
 * Formats a branch's distance into the display string used wherever a
 * branch is shown ("2.1 miles"). Brenda supplies the unit, so it's
 * never assumed — a missing value or unit yields null and callers
 * simply omit the line.
 */
export const formatBrendaBranchDistance = (
  distance: { value: number; unit: string } | null | undefined
): string | null => {
  if (typeof distance?.value !== 'number' || !distance.unit) return null

  return `${distance.value} ${distance.unit}`
}

export interface BrendaPickupPointStockResult {
  inStock: boolean
  requestedDate: string
  futureDate: string | null
}

/**
 * Resolves a single pickup branch's availability for the requested
 * parent qty, anchor date, and every linked child the user has in
 * their basket under this parent (mandatory and non-mandatory
 * alike — mandatory children are always added alongside their
 * parent, so they're already represented in the basket list).
 * Returns both the boolean verdict and the date the branch will
 * actually be available so the UI can render each branch's own
 * timeline without re-deriving the rules.
 *
 * Rules:
 *
 *   1. **Parent gate** — if the branch has neither enough current
 *      stock (`point.quantity >= required`) nor a
 *      `firstAvailableDate` of its own, the branch can't fulfil
 *      the order at all → `inStock: false`.
 *
 *   2. **Parent's effective date** — when the branch has stock
 *      now, the parent is available on `requestedDate`. When the
 *      branch has future stock, it's available on
 *      `point.firstAvailableDate`.
 *
 *   3. **Linked add-on bump** — every basket-linked child is
 *      evaluated: if it's *not* in stock at this branch
 *      (`linkedProductAvailability.maxQuantity < required`) and its
 *      own `firstAvailableDate` is later than the parent's effective
 *      date, the branch's "available from" is pushed to that date.
 *      A child that's in stock at the branch is available when the
 *      parent is, so it doesn't push the date — this is what stops a
 *      branch that actually holds the add-on (e.g. a mandatory extra
 *      at `maxQuantity: 999`) being wrongly bumped by the add-on's
 *      cross-branch `firstAvailableDate`. The branch's `futureDate`
 *      becomes the latest such date (if any), or the branch's own
 *      `firstAvailableDate`, or null.
 *
 * Used by the pickup modal to decide which branches are
 * selectable and what "Available from" date to show next to each
 * one.
 */
export const checkBrendaPickupPointStock = (
  point: BrendaChargesPickupPoint,
  required: number,
  requestedDate: string,
  addOns: BrendaChargesExtra[],
  basketLinkedStockCodes: string[] = []
): BrendaPickupPointStockResult => {
  // Parent gate — the branch must be able to fulfil the parent
  // either now or on a known future date, or it's a non-starter.
  const hasCurrentStock = point.quantity >= required
  const branchFutureDate = point.firstAvailableDate

  if (!hasCurrentStock && !branchFutureDate) {
    return { inStock: false, requestedDate, futureDate: null }
  }

  // The date the branch can actually offer the parent on. Future-
  // stock branches commit to their own `firstAvailableDate`;
  // current-stock branches commit to the anchor.
  const parentDate = branchFutureDate ?? requestedDate

  // Walk every basket-linked child and bump the branch's "available
  // from" date if a child can't be fulfilled when the parent lands.
  // A child is ignored when it's either already available by the
  // parent's date, or in stock at this branch (available now here,
  // regardless of its cross-branch future date).
  let latestFuture = branchFutureDate

  for (const stockCode of basketLinkedStockCodes) {
    const addOn = addOns.find(a => a.stockCode === stockCode)

    if (!addOn?.firstAvailableDate) continue
    if (addOn.firstAvailableDate <= parentDate) continue

    // In stock at this branch → available when the parent is, no bump.
    const atBranch = point.linked.find(l => l.stockCode === stockCode)
    if (atBranch && atBranch.maxQuantity >= required) continue

    if (!latestFuture || addOn.firstAvailableDate > latestFuture) {
      latestFuture = addOn.firstAvailableDate
    }
  }

  return { inStock: true, requestedDate, futureDate: latestFuture }
}
